import moment from "moment";
import * as React from "react";
import capitalize from "lodash/capitalize";
import { push } from "connected-react-router";
import { Box, Link, Typography } from "@mui/material";
import { connect, useDispatch, useSelector } from "react-redux";

import Can from "../Policy/Can";
import Filters from "./Filters";
import { RootState } from "../../store";
import * as reportsApi from "../../api/reports";
import { rupeeDisplay } from "../../helpers/rupee";
import { t, tl } from "../../components/translate";
import BillShow from "../Billing/BillShow/BillShow";
import ClientInfo from "../Client/ClientInfo/ClientInfo";
import { getCustomerNumber } from "../Client/ClientList";
import ClientCreateEdit from "../Client/ClientCreateEdit";
import { notificationAdd } from "../../actions/notification";
import { downloadExcel, downloadXML } from "../../helpers/files";
import { isSelfDataAccessEmployee } from "../NavigationBar/SideBar";
import { convertRichToPlain } from "../Lab/LabPrint/LabPrintFunctions";
import useIsReactNativeWebView from "../../hooks/useIsReactNativeWebView";
import useMobileScreen from "../../hooks/useMobileScreen";
import { DocumentTypes, PaymentBreakdown, VisitType } from "../../interfaces/BillInterfaces";
import * as calFns from "../../components/Calendar/functions/calendarFunctions";
import { convertADtoBS } from "../../components/Calendar/functions/calendarFunctions";
import List, { EmptyView, ListActions, Menu, MenuItem } from "../../components/OList";
import { formatDataForExcel, formatHeaderForExcel } from "../accounts/Reports/helper";
import ListHeaderShowHideDialog from "../../components/ListHeaderShowHideDialog/ListHeaderShowHideDialog";
import {
  getDefaultKeyValuesColumns,
  salesDefaultCols,
  salesHiddenCols
} from "../../components/ListHeaderShowHideDialog/helpers";
import useCurrentResourceCentre from "../../hooks/useCurrentResourceCentre";
import { useListColumnVisibility, COLUMN_STORAGE_KEYS } from "../../hooks/useListColumnVisibility";
import { getResourceCentreName } from "./DueReport";
import { useBeds } from "../../hooks/query/useBeds";
import { Bed } from "../../interfaces/BedInterface";
import { ResourceCentre } from "../../interfaces/ResourceCentreInterface";

// css
import "./Reports.scss";

interface SalesReportProps {
  isBranchReport?: boolean;
  actions: {
    navigateTo: (url) => void;
    addNotification: (statusCode) => void;
  };
}

interface DocRowProcessorInterface {
  billInfo: { billId: number; billNumber: string; related: null | string; type: string };
  billingDate: Date;
  clientFirstName: string;
  clientLastName: string;
  enteredBy: string;
  clientName: string;
  customerNumber: string;
  discount: number;
  grossAmount: number;
  paymentMethod: string;
  roundOffAmt: string;
  exemptSales: string;
  taxableSales: string;
  referrers: string;
  remarks: string;
  extraReferrer: string;
  services: Array<string>;
  status: string;
  taxAmount: number;
  taxableAmount: number;
  paidAmount: number;
  dueAmount: number;
  totalAmount: number;
  admissionDate: string;
  dischargeDate: string;
  bedName: string;
  registrationNo: string;
  // eslint-disable-next-line camelcase
  __meta__row_type?: string;
}

export const getPaymentMethodLabel = ({
  paymentMethod,
  paymentBreakdown
}: {
  paymentMethod: string | null;
  paymentBreakdown: Array<PaymentBreakdown>;
}): string => {
  // Old bills
  if (paymentMethod) {
    return paymentMethod;
  }

  // New bills (multiple methods)
  if (Array.isArray(paymentBreakdown) && paymentBreakdown?.length > 0) {
    return paymentBreakdown.map((p: PaymentBreakdown) => `${p.method}: ${p.amount}`).join(", ");
  }

  return "—";
};

const or = (val) => val || "";

const docColumns = () => [
  t("reports.customerNumber"),
  t("reports.client"),
  t("reports.clientFirstName"),
  t("reports.clientLastName"),
  t("registrationNo"),
  t("reports.billedBy"),
  t("reports.services"),
  t("reports.billNumber"),
  t("reports.billingDateBS"),
  t("reports.billingDateAD"),
  t("reports.grossAmount"),
  t("reports.discount"),
  t("reports.taxableAmount"),
  t("reports.taxAmount"),
  t("reports.roundOffAmt"),
  t("reports.paidAmount"),
  t("reports.dueAmount"),
  t("reports.totalAmount"),
  t("reports.taxableSales"),
  t("reports.exemptSales"),
  t("reports.remarks"),
  t("reports.payment.method"),
  t("reports.creditNoteFor"),
  t("reports.billStatus"),
  t("reports.referrer"),
  t("reports.extraReferrer"),
  t("reports.admissionDate"),
  t("reports.dischargeDate"),
  t("reports.bed")
];

const docRowProcessor = (
  {
    customerNumber,
    clientFirstName,
    clientLastName,
    registrationNo,
    clientName,
    billInfo,
    billingDate,
    grossAmount,
    discount,
    taxableAmount,
    taxAmount,
    paidAmount,
    totalAmount,
    roundOffAmt,
    services,
    taxableSales,
    exemptSales,
    remarks,
    paymentMethod,
    status,
    referrers,
    extraReferrer,
    enteredBy,
    admissionDate,
    dischargeDate,
    bedName,
    // eslint-disable-next-line camelcase
    __meta__row_type
  } = {} as DocRowProcessorInterface
) => {
  // eslint-disable-next-line camelcase
  if (__meta__row_type === "segment_summary") return "";
  const dueAmount = totalAmount - paidAmount || 0;
  return [
    or(getCustomerNumber(customerNumber)),
    or(clientName),
    or(clientFirstName),
    or(clientLastName),
    registrationNo,
    or(enteredBy),
    services,
    billInfo.billNumber,
    calFns.bsShortDate(billingDate),
    calFns.adShortDate(billingDate),
    grossAmount,
    discount,
    taxableAmount,
    taxAmount,
    roundOffAmt,
    paidAmount,
    dueAmount,
    totalAmount,
    taxableSales,
    exemptSales,
    convertRichToPlain(remarks),
    paymentMethod,
    or(billInfo.type === DocumentTypes.CREDITNOTE && billInfo.related?.billNumber),
    status,
    referrers,
    extraReferrer,
    admissionDate ? convertADtoBS(new Date(admissionDate)).formatted4 : "",
    dischargeDate ? convertADtoBS(new Date(dischargeDate)).formatted4 : "",
    bedName
  ];
};

const selectData = (item, beds: Bed[], resourceCentres: ResourceCentre[]) => ({
  clientName: `${capitalize(item.clientFirstName)} ${capitalize(item.clientLastName)}`,
  clientId: item.clientId,
  customerNumber: item.customerNumber,
  clientFirstName: item.clientFirstName,
  clientLastName: item.clientLastName,
  active: item.active,
  billInfo: {
    billNumber: item.billNumber,
    billId: item.billId,
    related: item.related,
    type: item.type
  },
  grossAmount: item.grossAmount,
  billingDate: new Date(item.issueDate),
  taxableAmount: item.taxableAmount,
  roundOffAmt: item.roundOffAmt,
  discount: item.discount,
  taxAmount: item.taxAmount,
  totalAmount: item.totalAmount,
  taxableSales: item.taxableSales,
  exemptSales: item.exemptSales,
  paymentMethod: getPaymentMethodLabel({
    paymentMethod: item.paymentMethod,
    paymentBreakdown: item.paymentBreakdown
  }),
  paymentBreakdown: item.paymentBreakdown,
  services: item.services,
  remarks: item.remarks,
  status: item.status,
  referrers: item.referrers,
  extraReferrer: item.extraReferrer,
  paidAmount: item.paidAmount,
  enteredBy: item.enteredBy,
  createdAt: item.createdAt,
  admissionDate: item.admissionDate,
  dischargeDate: item.dischargeDate,
  registrationNo: item.registrationNo,
  bedId: item.bedId,
  resourceCentreName: getResourceCentreName(resourceCentres, item.resourceCentreId),
  bedName: beds.find((bed: Bed) => bed.id === item.bedId)?.customName || "",
  vatAmount: item?.vatAmount || 0,
  healthEquityTaxAmount: item?.healthEquityTaxAmount || 0
});

const processData = (data, beds, resourceCentres) =>
  data.map((item) => selectData(item, beds, resourceCentres));

const SalesReport = ({ actions, isBranchReport }: SalesReportProps) => {
  // hooks
  const dispatch = useDispatch();
  const rc = useCurrentResourceCentre();
  const isMobileScreen = useMobileScreen();
  const { isRnWebView } = useIsReactNativeWebView();

  const { columns, setColumns } = useListColumnVisibility(
    COLUMN_STORAGE_KEYS.SALES_REPORT_COLUMNS,
    getDefaultKeyValuesColumns(
      [...salesDefaultCols, ...(isBranchReport ? ["resourceCentre"] : [])],
      salesHiddenCols
    )
  );

  // query hooks
  const { data: beds = [] } = useBeds();

  // selectors
  const userContext = useSelector((state: RootState) => state.userContext);

  // local states
  const [open, setOpen] = React.useState(false);
  const [reportData, setReportData] = React.useState([]);
  const [selectedBill, setSelectedBill] = React.useState(null);
  const [clientEditMode, setClientEditMode] = React.useState(false);
  const [selectedClientId, setSelectedClientId] = React.useState(null);
  const [isRequestLoading, setIsRequestLoading] = React.useState(false);
  const [showClientInfoPanel, setClientInfoPanel] = React.useState(false);
  const clientInfoHandle = () => {
    setSelectedClientId(null);
    setClientInfoPanel(false);
  };

  const currentResourceCentre = useCurrentResourceCentre();
  const resourceCentres = useSelector((state: RootState) => state.userBranch.collection) || [];

  const [filters, setFilters] = React.useState({
    from: calFns.startOfDay(new Date()).toDate(),
    until: new Date(),
    clientIds: [],
    billType: null,
    referrersName: [],
    employeeIds: [],
    billStatuses: ["billed"],
    visitType: VisitType.OPD,
    paymentMethods: [],
    counter: "",
    billNumber: "",
    ...(isBranchReport ? { resourceCentreIds: [currentResourceCentre?.id] } : {})
  });

  // memoized values
  const total = React.useMemo(
    () =>
      reportData.reduce(
        (acc, cur) => ({
          amount: acc.amount + (cur?.totalAmount || 0),
          paid: acc.paid + (cur?.paidAmount || 0),
          gross: acc.gross + (cur?.grossAmount || 0),
          discount: acc.discount + (cur?.discount || 0),
          vat: acc.vat + (cur?.vatAmount || 0),
          het: acc.het + (cur?.healthEquityTaxAmount || 0)
        }),
        { paid: 0, amount: 0, gross: 0, discount: 0, vat: 0, het: 0 }
      ),
    [reportData]
  );

  // helpers
  const totalDueAmount = total.amount - total.paid;
  const permissionGroup =
    userContext && userContext.userCreds.userGroups && userContext.userCreds.userGroups[0];
  const hideEmployeeSelect = isSelfDataAccessEmployee(permissionGroup);

  const applyFilters = (newFilters) => {
    setFilters(newFilters);
  };

  // effect hooks
  React.useEffect(() => {
    setIsRequestLoading(true);
    reportsApi
      .salesReport(filters)
      .then((data) => {
        setReportData(processData(data, beds, resourceCentres));
      })
      .catch((err) => {
        dispatch(
          notificationAdd({
            id: new Date().getUTCMilliseconds(),
            variant: "error",
            message: err?.data?.message || "Failed to fetch sales report.",
            autoTimeout: true
          })
        );
      })
      .finally(() => setIsRequestLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return (
    <Can policyAccessKey="report:salesReport">
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column"
        }}
      >
        {/* filter */}
        <Box sx={{ px: 3, py: 2 }}>
          <Filters
            filters={filters}
            onSetFilters={(newFilters) => applyFilters(newFilters)}
            hideEmployeeSelect={hideEmployeeSelect}
            isRequestLoading={isRequestLoading}
          />
        </Box>

        {/* list */}
        <Box
          className="salesReportList"
          sx={{
            borderTop: 1,
            borderColor: "divider",
            width: "100%",
            overflow: "auto"
          }}
        >
          <Box
            sx={{
              minWidth: isMobileScreen || isRnWebView ? "1600px" : "auto",
              mb: "40px"
            }}
          >
            <List
              automation="salesReportList"
              data={reportData}
              rowHeight={50}
              defaultSortColumn="billingDate"
              defaultSortOrder={-1}
              activeRow={1}
              adjustHeightToContents
              columns={[
                ...(isBranchReport
                  ? [
                      {
                        key: "resourceCentre",
                        label: "Branch",
                        formatter: ({ resourceCentreName }) => (
                          <Typography>{resourceCentreName}</Typography>
                        ),
                        segmentable: true,
                        segmentBy: (row) => row.resourceCentreName,
                        sortable: true,
                        sortFunction: (a, b) =>
                          or(a.resourceCentreName).toLowerCase() >
                          or(b.resourceCentreName).toLowerCase()
                            ? 1
                            : -1
                      }
                    ]
                  : []),
                {
                  key: "clientName",
                  label: tl("reports.client"),
                  sortable: true,
                  sortFunction: (a, b) =>
                    or(a.clientName).toLowerCase() > or(b.clientName).toLowerCase() ? 1 : -1,
                  formatter: ({
                    clientFirstName,
                    clientLastName,
                    clientId,
                    customerNumber,
                    active
                  }) => (
                    <Typography
                      component="div"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "flex-start"
                      }}
                    >
                      <Box
                        style={{
                          textDecoration: `${active ? "underline" : ""}`,
                          cursor: "pointer",
                          width: "100%",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}
                        onClick={() => {
                          if (active) {
                            setSelectedClientId(clientId);
                            setClientInfoPanel(true);
                          }
                        }}
                      >
                        {`${clientFirstName} ${clientLastName}`.toUpperCase()}
                        {active ? "" : "(deleted)"}
                      </Box>
                      <Box fontSize="small" color="gray">
                        {getCustomerNumber(customerNumber)}
                      </Box>
                    </Typography>
                  )
                },
                {
                  key: "billInfo",
                  label: tl("reports.billNumber"),
                  sortable: true,
                  sortFunction: (a, b) =>
                    or(a.billInfo.billNumber).toLowerCase() >
                    or(b.billInfo.billNumber).toLowerCase()
                      ? 1
                      : -1,
                  formatter: ({ billInfo }) => (
                    <Box flex={1} flexDirection="column" alignItems="center" component={Typography}>
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <Link onClick={() => setSelectedBill(billInfo.billId)}>
                        {billInfo.billNumber}
                      </Link>
                      {billInfo.type === DocumentTypes.CREDITNOTE && billInfo.related && (
                        <Box fontSize="small" color="gray">
                          CN {billInfo.related.billNumber}
                        </Box>
                      )}
                    </Box>
                  )
                },
                {
                  key: "billingDate",
                  label: tl("reports.billingDate"),
                  formatter: ({ billingDate, createdAt }) => (
                    <Box>
                      <Typography>{calFns.bsFullDate(billingDate)}</Typography>
                      {createdAt && !moment(billingDate).isSame(createdAt, "day") && (
                        <Typography fontSize="0.8rem" color="gray">
                          {calFns.bsFullDate(createdAt)}
                        </Typography>
                      )}
                    </Box>
                  ),
                  sortable: true,
                  sortFunction: (a, b) =>
                    new Date(a.billingDate) > new Date(b.billingDate) ? 1 : -1
                },
                {
                  key: "services",
                  label: "Services",
                  formatter: ({ services }) => <Typography>{services}</Typography>
                },
                {
                  key: "enteredBy",
                  label: "Billed by",
                  formatter: ({ enteredBy }) => <Typography>{enteredBy}</Typography>
                },
                {
                  key: "registrationNo",
                  label: "Registration No",
                  formatter: ({ registrationNo }) => <Typography>{registrationNo}</Typography>
                },
                {
                  key: "creditNoteFor",
                  label: "CN For",
                  formatter: ({ billInfo }) => (
                    <Typography>
                      {billInfo.type === DocumentTypes.CREDITNOTE
                        ? billInfo.related?.billNumber
                        : ""}
                    </Typography>
                  )
                },
                {
                  key: "paymentMethod",
                  sortable: true,
                  segmentable: true,
                  label: "Payment Method",
                  formatter: ({ paymentMethod, paymentBreakdown }) => (
                    <Typography>
                      {getPaymentMethodLabel({ paymentMethod, paymentBreakdown })}
                    </Typography>
                  )
                },

                {
                  key: "admissionDate",
                  label: "Admission Date",
                  formatter: ({ admissionDate }) => <Typography>{admissionDate || ""}</Typography>
                },
                {
                  key: "dischargeDate",
                  label: "Discharge Date",
                  formatter: ({ dischargeDate }) => <Typography>{dischargeDate || ""}</Typography>
                },
                {
                  key: "bed",
                  label: "Bed",
                  formatter: ({ bedName }) => <Typography>{bedName}</Typography>
                },
                {
                  key: "discount",
                  label: "Discount",
                  formatter: ({ discount }) => <Typography ml="20px">{discount}</Typography>
                },
                {
                  key: "grossAmount",
                  label: "Gross Total",
                  formatter: ({ grossAmount }) => (
                    <Typography>{rupeeDisplay(grossAmount)}</Typography>
                  )
                },
                {
                  key: "vatAmount",
                  label: "VAT Amount",
                  formatter: ({ vatAmount }) => <Typography>{rupeeDisplay(vatAmount)}</Typography>
                },
                {
                  key: "hetAmount",
                  label: "HET Amount",
                  formatter: ({ healthEquityTaxAmount }) => (
                    <Typography>{rupeeDisplay(healthEquityTaxAmount)}</Typography>
                  )
                },
                {
                  key: "paidAmount",
                  label: tl("reports.paidAmount"),
                  formatter: ({ paidAmount }) => (
                    <Typography>{paidAmount ? rupeeDisplay(paidAmount || 0) : 0}</Typography>
                  )
                },
                {
                  key: "dueAmount",
                  label: tl("reports.dueAmount"),
                  formatter: ({ paidAmount, totalAmount }) => {
                    const dueAmount = totalAmount - (paidAmount || 0);
                    return <Typography>{dueAmount ? rupeeDisplay(dueAmount) : 0}</Typography>;
                  }
                },
                {
                  key: "totalAmount",
                  label: tl("reports.total"),
                  formatter: ({ totalAmount }) => (
                    <Typography>{totalAmount ? rupeeDisplay(totalAmount) : ""}</Typography>
                  )
                }
              ].filter((row) => columns[row.key])}
              segementSummaryRenderer={(acc) => (
                <Box style={{ background: "#e6e6e6" }} display="flex" flexGrow={1}>
                  <Box display="flex" flexGrow={1} padding="10px 20px">
                    {acc.segment || "N/A"}
                  </Box>
                  <Box flexBasis="200px" pt="10px" textAlign="right">
                    {rupeeDisplay(
                      Math.floor(acc.rows.reduce((prev, cur) => prev + cur.grossAmount, 0) || 0)
                    )}
                  </Box>
                </Box>
              )}
            >
              <EmptyView>
                <Box textAlign="center" padding="50px">
                  No items to show.
                </Box>
              </EmptyView>
              <ListActions>
                {({ getProcessedData }) => (
                  <Menu>
                    <MenuItem
                      onClick={async () => {
                        try {
                          await downloadExcel(
                            "Sales Report",
                            formatHeaderForExcel(docColumns()),
                            formatDataForExcel(getProcessedData(), docRowProcessor),
                            rc,
                            {
                              from: filters.from,
                              until: filters.until
                            }
                          );
                        } catch (err) {
                          dispatch(
                            notificationAdd({
                              id: new Date().getUTCMilliseconds(),
                              variant: "error",
                              message: "Failed to download Excel.",
                              autoTimeout: true
                            })
                          );
                        }
                      }}
                    >
                      {tl("reports.excel")}
                    </MenuItem>
                    <MenuItem
                      onClick={() =>
                        reportsApi
                          .getRevenueTally(filters)
                          .then((response) => {
                            downloadXML(
                              `${t("reports.salesReport")}-${
                                calFns.convertADtoBS(filters.from).formatted
                              }-${calFns.convertADtoBS(filters.until).formatted}`,
                              response.data
                            );
                            actions.addNotification(response.status);
                          })
                          .catch(() => actions.addNotification(500))
                      }
                    >
                      {tl("reports.tally")}
                    </MenuItem>
                    <MenuItem onClick={() => setOpen(true)}>Show/Hide Columns</MenuItem>
                  </Menu>
                )}
              </ListActions>
            </List>

            <Box
              className="salesReportTotalBarStyle"
              sx={{ height: "40px", position: "fixed", bottom: 0 }}
            >
              <Box display="flex" paddingLeft="20px" className="totalTxt">
                {t("reports.total")}
              </Box>

              {columns?.discount && (
                <Typography sx={{ width: "150px", textAlign: "right" }}>
                  {rupeeDisplay(Number.parseFloat(total.discount).toFixed(2) || 0)}
                </Typography>
              )}

              <Typography sx={{ width: "150px", textAlign: "right" }}>
                {rupeeDisplay(Number.parseFloat(total.gross).toFixed(2) || 0)}
              </Typography>

              <Typography sx={{ width: "150px", textAlign: "right" }}>
                {rupeeDisplay(Number.parseFloat(total.vat).toFixed(2) || 0)}
              </Typography>

              <Typography sx={{ width: "150px", textAlign: "right" }}>
                {rupeeDisplay(Number.parseFloat(total.het).toFixed(2) || 0)}
              </Typography>

              <Typography sx={{ width: "150px", textAlign: "right" }}>
                {rupeeDisplay(Number.parseFloat(total.paid).toFixed(2) || 0)}
              </Typography>

              <Typography sx={{ width: "160px", textAlign: "right" }}>
                {rupeeDisplay(totalDueAmount.toFixed(2) || 0)}
              </Typography>

              <Typography sx={{ width: "170px", textAlign: "right" }}>
                {rupeeDisplay(Number.parseFloat(total.amount).toFixed(2) || 0)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* panels and dialogs */}
        <>
          <ListHeaderShowHideDialog
            onChange={setColumns}
            open={open}
            onClose={() => setOpen(false)}
            columns={columns}
          />

          {selectedBill && (
            <BillShow
              billId={selectedBill}
              handleViewClose={() => setSelectedBill(null)}
              hideBillingActions
            />
          )}

          {showClientInfoPanel &&
            (clientEditMode ? (
              <ClientCreateEdit
                setEditMode={setClientEditMode}
                clientId={selectedClientId}
                mode="edit"
                onCancel={() => {
                  setClientEditMode(false);
                }}
                stayOnCurrentPage
              />
            ) : (
              <ClientInfo
                id={selectedClientId}
                handleViewClose={clientInfoHandle}
                stayOnCurrentPage
                setEditMode={setClientEditMode}
              />
            ))}
        </>
      </Box>
    </Can>
  );
};

export default connect(null, (dispatch) => ({
  actions: {
    navigateTo: (url) => dispatch(push(url)),
    addNotification: (statusCode) => {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: statusCode === 200 ? "success" : "error",
          message:
            statusCode === 200 ? tl("reports.downloadSuccess") : tl("reports.downloadFailure"),
          autoTimeout: true
        })
      );
    }
  }
}))(SalesReport);
